/*
  Recently Viewed rail.

  localStorage holds product IDS ONLY. Everything a card states - vendor,
  rating, price, stock - is rendered server-side at view time through
  `card-product`, the shared card snippet, so nothing on this rail can be
  staler than the request that drew it. The previous implementation stored a
  full snapshot per product and rebuilt cards from a JS string template, which
  is why the rail could show a price from weeks ago and could never have shown
  a stock count honestly.

  The cards come from one Section Rendering API call:

    /search?section_id=<id>&type=product&q=id:1 OR id:2 OR id:3

  Search answers by relevance, so the returned cards are reordered against the
  ids we asked for. Most recent first.
*/
(function saikospeedRecentlyViewed() {
  'use strict';

  var STORAGE_KEY = 'saiko:recently-viewed';
  // The snapshot format this rail used to write. Read once so a shopper's
  // history survives the change; never written back.
  var LEGACY_KEY = 'recentlyViewedProduct';
  var MAX_STORED = 12;

  /*
    Ids as strings throughout. localStorage round-trips everything to text and
    the card markup carries them in a data attribute, so normalising once here
    means the two are never compared across types.
  */
  function normalizeIds(list) {
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var raw = list[i];
      if (raw === null || raw === undefined) continue;
      var id = String(typeof raw === 'object' ? raw.productId || raw.id || '' : raw).trim();
      if (!id || !/^\d+$/.test(id)) continue;
      if (seen[id]) continue;
      seen[id] = true;
      out.push(id);
    }
    return out;
  }

  function readIds(storage) {
    if (!storage) return [];
    var parsed = null;
    try {
      parsed = JSON.parse(storage.getItem(STORAGE_KEY));
    } catch (error) {
      parsed = null;
    }
    if (Array.isArray(parsed)) return normalizeIds(parsed);

    // No new-format history yet: lift the ids out of the old snapshot array.
    try {
      var legacy = JSON.parse(storage.getItem(LEGACY_KEY));
      if (Array.isArray(legacy)) return normalizeIds(legacy);
    } catch (error) {
      /* A corrupt or absent legacy entry just means no history. */
    }
    return [];
  }

  function writeIds(storage, ids) {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, MAX_STORED)));
    } catch (error) {
      /* Private mode or a full quota: the rail degrades to empty, not broken. */
    }
  }

  /* The viewed product goes to the front, and appears exactly once. */
  function recordId(existing, id) {
    return normalizeIds([id].concat(existing || [])).slice(0, MAX_STORED);
  }

  function buildQuery(ids) {
    return ids
      .map(function toClause(id) {
        return 'id:' + id;
      })
      .join(' OR ');
  }

  function buildUrl(searchUrl, sectionId, ids) {
    return (
      searchUrl +
      '?section_id=' +
      encodeURIComponent(sectionId) +
      '&type=product&q=' +
      encodeURIComponent(buildQuery(ids))
    );
  }

  /*
    Restore the caller's order. Anything search returned that we did not ask
    for is dropped rather than appended: the rail is a record of what this
    shopper looked at, and a relevance match is not that.
  */
  function orderById(items, ids) {
    var byId = {};
    for (var i = 0; i < items.length; i++) {
      var key = String(items[i].id);
      if (key && !byId[key]) byId[key] = items[i];
    }
    var out = [];
    for (var j = 0; j < ids.length; j++) {
      if (byId[ids[j]]) out.push(byId[ids[j]]);
    }
    return out;
  }

  if (typeof document === 'undefined') {
    module.exports = { normalizeIds: normalizeIds, recordId: recordId, buildQuery: buildQuery, buildUrl: buildUrl, orderById: orderById, STORAGE_KEY: STORAGE_KEY, LEGACY_KEY: LEGACY_KEY, MAX_STORED: MAX_STORED };
    return;
  }

  function safeStorage() {
    try {
      var probe = window.localStorage;
      probe.getItem(STORAGE_KEY);
      return probe;
    } catch (error) {
      return null;
    }
  }

  if (!customElements.get('recently-viewed-rail')) {
    customElements.define('recently-viewed-rail', class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        var storage = safeStorage();
        var currentId = String(this.dataset.currentProductId || '').trim();
        // Read the history BEFORE recording this visit, so the product being
        // looked at cannot appear in its own rail.
        var stored = readIds(storage);

        if (currentId) writeIds(storage, recordId(stored, currentId));

        var count = parseInt(this.dataset.count, 10);
        if (!Number.isFinite(count) || count < 1) count = 5;

        var wanted = stored
          .filter(function notCurrent(id) {
            return id !== currentId;
          })
          .slice(0, count);

        if (wanted.length) this.render(wanted);
      }

      render(ids) {
        var rail = this;
        var grid = this.querySelector('.recently-viewed-grid');
        if (!grid) return;

        fetch(buildUrl(this.dataset.searchUrl || '/search', this.dataset.sectionId || '', ids))
          .then(function onResponse(response) {
            if (!response.ok) throw new Error('recently viewed: ' + response.status);
            return response.text();
          })
          .then(function onHtml(html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var nodes = Array.prototype.slice.call(
              parsed.querySelectorAll('[data-recently-viewed-results] > li[data-product-id]')
            );
            var ordered = orderById(
              nodes.map(function withId(node) {
                return { id: node.dataset.productId, node: node };
              }),
              ids
            );
            if (!ordered.length) return;

            ordered.forEach(function append(entry) {
              grid.appendChild(document.importNode(entry.node, true));
            });
            rail.hidden = false;
          })
          .catch(function onFailure() {
            /*
              A rail that cannot prove what it shows shows nothing. It stays
              hidden rather than falling back to a stored snapshot, which is
              the exact staleness this rebuild exists to remove.
            */
          });
      }
    });
  }
}());
